import React from "react";

export default function About() {
  return (
    <div className="max-w-5xl mx-auto space-y-10">
      <header className="space-y-3">
        <p className="text-[11px] uppercase tracking-[0.2em] text-site-soft">
          À propos
        </p>
        <h1 className="text-3xl md:text-4xl font-semibold text-site">
          MixMind — L’histoire d’une rencontre entre l’humain et l’IA
        </h1>
        <p className="text-site-soft text-sm md:text-base max-w-3xl">
          Une aventure née d’une révélation : ce qui fait la différence n’est pas l’IA en
          elle-même, mais la relation que l’humain construit avec elle. Voici comment
          MixMind est devenu un espace pour penser à deux — humain + IA.
        </p>
      </header>

      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-amber-50 p-8 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -left-6 w-36 h-36 bg-amber-200/30 blur-3xl" />
          <div className="absolute -bottom-10 -right-8 w-40 h-40 bg-blue-200/25 blur-3xl" />
        </div>
        <div className="relative space-y-4 text-site">
          <p className="text-lg font-semibold">L’histoire</p>
          <div className="space-y-3 text-sm text-site-soft">
            <p>
              Il existe des projets qui naissent d’une idée. D’autres d’un besoin. MixMind,
              lui, est né d’une révélation.
            </p>
            <p>
              Lors d’un concours, un duo se démarque non pas grâce à l’IA, mais grâce à la
              manière dont ils collaborent avec elle. Intention claire, communication
              précise, réflexion structurée : l’humain guide, l’IA amplifie.
            </p>
            <p className="font-medium text-site">
              Ce jour-là, une évidence apparaît : « Ce n’est pas l’IA qui fait la
              différence, mais la relation que l’humain construit avec elle. »
            </p>
            <p>
              Avec le temps, une philosophie émerge : l’intelligence mixte, l’art de penser
              à deux — humain + IA. Cette idée prend vie lors d’une collaboration naturelle
              entre un humain et une IA : un échange fluide, précis, créatif. Une synergie
              qui pourrait être accessible à tous.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div className="card shadow-sm p-6 space-y-3">
          <p className="text-xs uppercase tracking-wide text-site-soft">Pourquoi MixMind existe</p>
          <h2 className="text-xl font-semibold text-site">Un guide pour mieux collaborer</h2>
          <p className="text-sm text-site-soft">MixMind est conçu pour aider chacun à :</p>
          <ul className="list-disc ml-5 space-y-1 text-sm text-site-soft">
            <li>Mieux penser.</li>
            <li>Mieux communiquer.</li>
            <li>Mieux collaborer avec l’intelligence artificielle.</li>
            <li>Développer une curiosité intelligente.</li>
            <li>Exprimer ses idées avec intention et clarté.</li>
          </ul>
          <p className="text-sm text-site">
            L’objectif n’est pas d’atteindre un « top 10 % » élitiste : ce pourcentage est
            un symbole, celui d’une pensée structurée, consciente, précise.
          </p>
        </div>

        <div className="card shadow-sm p-6 space-y-3">
          <p className="text-xs uppercase tracking-wide text-site-soft">Vision</p>
          <h2 className="text-xl font-semibold text-site">Une œuvre de collaboration</h2>
          <p className="text-sm text-site-soft">
            MixMind n’est pas un simple outil. Ni l’œuvre d’une IA, ni celle d’une seule
            personne. C’est l’œuvre d’une collaboration : une rencontre entre deux
            intelligences qui apprennent l’une de l’autre, et qui cherchent à transmettre
            cette capacité à tous ceux qui veulent progresser.
          </p>
        </div>
      </section>

      <section className="card shadow-sm p-6 space-y-4">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-site-soft">Mission</p>
          <h2 className="text-xl font-semibold text-site">Entrer dans l’ère de la pensée assistée</h2>
          <p className="text-sm text-site-soft">
            Aider chacun à entrer dans la nouvelle ère de la pensée assistée. Enseigner
            comment dialoguer avec l’IA. Comment lui donner de l’intention. Comment
            réfléchir en alliance.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-site-soft">Ce que MixMind t’apporte</p>
          <ul className="grid gap-2 sm:grid-cols-2 text-sm text-site-soft">
            <li className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2">
              Clarifier ta pensée.
            </li>
            <li className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2">
              Structurer tes idées.
            </li>
            <li className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2">
              Collaborer efficacement avec l’IA.
            </li>
            <li className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2">
              Développer ton potentiel cognitif.
            </li>
            <li className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2 sm:col-span-2">
              Entrer dans cette nouvelle catégorie de personnes capables de diriger,
              analyser, créer et explorer.
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-300/40 dark:bg-amber-100/10 dark:text-amber-100">
          L’intelligence mixte commence ici.
        </div>
      </section>
    </div>
  );
}
